import React from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MapPin, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface StateProvinceWarningProps {
  userCountry: string | null;
  userState: string | null;
  practitionerCountry: string | null;
  practitionerState: string | null;
  className?: string;
}

export const StateProvinceWarning: React.FC<StateProvinceWarningProps> = ({
  userCountry,
  userState,
  practitionerCountry,
  practitionerState,
  className = ""
}) => {
  const { t } = useTranslation('booking');
  
  // Only show warning for US or Canada when states/provinces differ
  const shouldShowWarning = 
    (userCountry === 'US' || userCountry === 'CA') &&
    userState && 
    practitionerState &&
    userState !== practitionerState;
  
  if (!shouldShowWarning) {
    return null;
  }
  
  const regionLabel = userCountry === 'US' ? 'State' : 'Province';
  
  return (
    <Alert className={`border-blue-200 bg-blue-50 ${className}`}>
      <AlertTriangle className="h-4 w-4 text-blue-600" />
      <AlertDescription className="text-blue-800">
        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">{t('stateProvinceMismatch.title')}</p>
            <div className="text-sm space-y-1">
              <div>
                {t('stateProvinceMismatch.yourLocation', { region: regionLabel })}: <span className="font-medium">{userState}</span>
              </div>
              <div>
                {t('stateProvinceMismatch.practitionerLocation', { region: regionLabel })}: <span className="font-medium">{practitionerState}</span>
              </div>
            </div>
            <p className="text-xs mt-2">
              {t('stateProvinceMismatch.insuranceNote')}
            </p>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
